import {ChangeDetectionStrategy, Component, computed, Injectable, signal} from "@angular/core";
import {BrukerId, KommentarId, KommentarModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Temporal} from "temporal-polyfill";
import {KommentarDatatjeneste} from "felles-ng2/tjenester/datalager/KommentarDatatjeneste";
import {plainDateFraZonedDateTime, zonedDateTimeFørsteDagIMåneden} from "felles-ng2/støttefunksjoner/temporal";
import {kommentarerGruppertMånedsvis, Kommentargruppe, varsledeEksTrådForfatter} from "felles-ng2/modell/kommentarModellHjelpere";
import {GjeldendeTidssone} from "felles-ng2/tjenester/i18n/GjeldendeTidssone";
import {FormaterPlainDatePipe} from "felles-ng2/pipes/FormaterPlainDatePipe";
import {AvatarKomponent} from "web-ng2/delt/komponenter/avatar/avatar";
import {FormaterZonedDateTimePipe} from "felles-ng2/pipes/FormaterZonedDateTimePipe";
import {MatTooltip} from "@angular/material/tooltip";
import {TranslateModule} from "@ngx-translate/core";
import {NgClass} from "@angular/common";
import {første} from "felles-ng2/støttefunksjoner/lister";
import {KommentarTraadKomponent} from "../kommentar-traad/kommentar-traad";
import {MatButton} from "@angular/material/button";
import {VisSpinnerDirektiv} from "web-ng2/delt/direktiver/button--visSpinner/button--visSpinner";
import {BehaviorSubject, mergeMap, Observable, Subject, switchMap, tap} from "rxjs";
import {map} from "rxjs/operators";
import {DataOgMeta} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {ForelderstiKomponent} from "web-ng2/delt/komponenter/foreldersti/foreldersti";
import {KlikkbareLenkerPipe} from "felles-ng2/pipes/KlikkbareLenkerPipe";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {MatTab, MatTabGroup} from "@angular/material/tabs";
import {PlainDatePeriode} from "felles-ng2/støttefunksjoner/plainDatePerioder";
import {ZonedDateTimePeriode} from "felles-ng2/støttefunksjoner/zonedDateTimePerioder";
import {Klokke} from "felles-ng2/tjenester/Klokke";
import Duration = Temporal.Duration;

const debug = initDebugger('KommentarerKomponent');

@Component({
    selector: 'kommentarer',
    templateUrl: 'kommentarer.html',
    styleUrl: 'kommentarer.scss',
    imports: [
        MatTab,
        MatButton,
        MatTabGroup,
        NgClass,
        TranslateModule,
        MatTooltip,
        FormaterPlainDatePipe,
        FormaterZonedDateTimePipe,
        KlikkbareLenkerPipe,
        AvatarKomponent,
        KommentarTraadKomponent,
        VisSpinnerDirektiv,
        ForelderstiKomponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class KommentarerKomponent {
    static ajsSelector = 'kommentarer';

    /** angir hvor mange måneder vi laster av gangen */
    static standardPeriode = Duration.from({months: 3});

    /** kun mine kommentarer = 0, kun andres kommentarer = 1*/
    utvalg = signal<0 | 1>(0);
    /** kommentarer gruppert etter måned, settes via {@link #lastKommentarer} */
    grupper = signal<Kommentargruppe[]>([]);
    valgtKommentarId = signal<KommentarId | null>(null);
    valgtKommentar = computed(() => this.beregnValgtKommentar());
    laster = signal(false);

    /** Perioden vi sist lastet data for. Beveges bakover i tid via {@link #lastFlere} */
      //public for testbarhet
    sistLastetPeriode!: ZonedDateTimePeriode;
    /** Subject som sørger for å laste kommentarer og gruppere disse månedsvis.
     * Hver gang en periode levers til denne lastes data fra server og grupperes etter måned. */
    private lastKommentarer = new Subject<ZonedDateTimePeriode>();

    constructor(private avhengigheter: KommentarerKomponentAvhengigheter, private navigasjon: Navigasjon, private gjeldendeTidssone: GjeldendeTidssone, private klokke: Klokke, private kontekst: BrukerkontekstTilbyder) {
        toObservable(this.utvalg)
          .pipe(
            //når utvalg endres må gammel subscription termineres og ny opprettes, dette sørger switchMap for
            switchMap(utvalg => {
                this.grupper.set([]);
                this.sistLastetPeriode = this.initiellPeriode();
                this.lastKommentarer = new BehaviorSubject<ZonedDateTimePeriode>(this.sistLastetPeriode); //må bruke BehaviorSubject for at initiell periode skal leveres
                return this.lastKommentarer.pipe(
                  //vis 'laster'-spinner
                  tap(periode => this.laster.set(true)),
                  //hver gang vi dytter ny periode til this.lastKommentarer starter vi en ny observer for denne perioden
                  //merk at observere opprettet for tidligere perioder fortsatt aktive og vil lever verdier ved endringer i datalager
                  mergeMap(periode => this.hentKommentarer(periode)
                    .pipe(
                      //skjul 'laster'-spinner når data har blitt levert fra xhr-kall
                      tap(dataOgMeta => !dataOgMeta.meta.levertFraCache ? this.laster.set(false) : null),
                      map(dataOgMeta => ({kommentarer: dataOgMeta.data, periode: periode})))),
                  //grupper kommentarer knyttet til perioden på måned
                  map(utvalg => kommentarerGruppertMånedsvis(utvalg.kommentarer, this.periodeSomPlainDate(utvalg.periode)))
                )
            }),
            takeUntilDestroyed()
          )
          .subscribe(oppdaterteGrupper => this.oppdaterGrupper(oppdaterteGrupper))
    }

    varsledeEksTraadForfatter(kommentar: KommentarModell) {
        return varsledeEksTrådForfatter(kommentar);
    }

    senestePublisertAv(kommentar: KommentarModell) {
        return kommentar.senesteSvar?.publisertAv ?? kommentar.publisertAv;
    }

    setValgt(kommentar: KommentarModell) {
        this.valgtKommentarId.set(kommentar.id);
    }

    lastFlere() {
        this.sistLastetPeriode = this.beregnNestePeriode();
        this.lastKommentarer.next(this.sistLastetPeriode);
    }

    gaaTilForelder() {
        this.navigasjon.gåTilEntitetRef(this.valgtKommentar()!.forelder.entitetRef);
    }

    // private hjelpemetoder

    private beregnValgtKommentar(): KommentarModell | null {
        const alleKommentarer = this.grupper()
          .flatMap(gruppe => gruppe.traader);
        return alleKommentarer
          .find(kommentar => kommentar.id == this.valgtKommentarId()) ?? første(alleKommentarer)
    }

    private hentKommentarer(periode: ZonedDateTimePeriode): Observable<DataOgMeta<KommentarModell[]>> {
        return this.utvalg() == 0 ?
          this.avhengigheter.mineKommentarer(periode, this.kontekst.autentisertBruker().bruker.id) :
          this.avhengigheter.andresKommentarer(periode, this.kontekst.autentisertBruker().bruker.id);
    }

    /** Oppdaterer {@link #grupper} med nye/oppdaterte kommentargrupper */
    private oppdaterGrupper(oppdaterteGrupper: Kommentargruppe[]) {
        //putter alle gruppene i en map - slik at oppdaterteGrupper overskriver eksisterende grupper i this.grupper
        const perioder = new Map<string, Kommentargruppe>();
        [...this.grupper(), ...oppdaterteGrupper].forEach(gruppe => perioder.set(gruppe.dato.toString(), gruppe));
        this.grupper.set(Array.from(perioder.values()));
    }

    /** Returnerer angitt ZonedDateTimePeriode (som brukes ved henting av kommentarer fra server)
     * som {@link PlainDatePeriode}, med slutt satt til dagen før slutt i angitt periode - dette
     * da vi ved spørring mot server behandler periode som 'fra og med' - 'til' (altså ikke 'til og med')*/
    private periodeSomPlainDate(periode: ZonedDateTimePeriode): PlainDatePeriode {
        return {
            start: plainDateFraZonedDateTime(periode.start, this.gjeldendeTidssone.brukersTidssone()),
            slutt: plainDateFraZonedDateTime(periode.slutt, this.gjeldendeTidssone.brukersTidssone()).subtract({days: 1})
        };
    }

    /** Første periode vi henter data for, inneværende måned og de to foregående.
     * Merk at vi definerer periode som ['fra og med', 'til'] */
    //public for testbarhet
    initiellPeriode(): ZonedDateTimePeriode {
        const nå = this.klokke.nåSomZonedDateTime().withTimeZone(this.gjeldendeTidssone.brukersTidssone().somZoneId());
        const startenAvNesteMåned = zonedDateTimeFørsteDagIMåneden(nå).add({months: 1});
        return {
            start: startenAvNesteMåned.subtract(KommentarerKomponent.standardPeriode),
            slutt: startenAvNesteMåned
        };
    }

    /** Returnerer neste periode, dvs perioden før (bakover i tid) {@link #sistLastetPeriode}.
     * Merk at vi definerer periode som ['fra og med', 'til'] */
    private beregnNestePeriode() {
        return {
            start: this.sistLastetPeriode.start.subtract(KommentarerKomponent.standardPeriode),
            slutt: this.sistLastetPeriode.start
        };
    }
}

@Injectable()
export class KommentarerKomponentAvhengigheter {

    constructor(private kommentarDatatjeneste: KommentarDatatjeneste) {
    }

    mineKommentarer(periode: ZonedDateTimePeriode, brukerId: BrukerId): Observable<DataOgMeta<KommentarModell[]>> {
        return this.kommentarDatatjeneste.mineKommentarerMedMeta(periode, brukerId);
    }

    andresKommentarer(periode: ZonedDateTimePeriode, brukerId: BrukerId): Observable<DataOgMeta<KommentarModell[]>> {
        return this.kommentarDatatjeneste.andresKommentarerMedMeta(periode, brukerId);
    }
}
